/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { useEffect, useRef } from "react"
import { useLocation } from "react-router-dom"

import { ddLogCustomError } from "utils/datadog"
import { setBottomNavVisibility } from "utils/nativeBridge"
import { isWebview } from "utils/webview"
import { sendWebviewBottomNavErrorEvent } from "utils/webviewTelemetry"

import { getEcomBottomNavVisible } from "./useAppBottomNavVisibility"

// Hides the app bottom nav while `open` is true; when closed, restores visibility based on the current route.
export const useHideAppBottomNavWhileBottomSheetOpen = (
  open: boolean,
  source: string,
): void => {
  const { pathname } = useLocation()

  const pathnameRef = useRef(pathname)
  pathnameRef.current = pathname

  const sourceRef = useRef(source)
  sourceRef.current = source

  // Only the hide call (open === true) and the matching restore (open === false
  // after a hide) should reach the bridge. A mount with open === false would
  // just repeat useAppBottomNavVisibility's assertion for this route, and on a
  // build where the app refuses the handler it would emit a spurious error
  // record — so skip it. Each hide/restore rejection is reported individually:
  // a hide reject is a failed "special request" to drop the bar, a restore
  // reject leaves the bar contradicting the screen, and both are discrete
  // events we want to count.
  const hidRef = useRef(false)

  useEffect(() => {
    if (!isWebview()) return

    const shouldHide = open
    const shouldRestore = !open && hidRef.current
    if (!shouldHide && !shouldRestore) return

    const visible = shouldHide
      ? false
      : getEcomBottomNavVisible(pathnameRef.current)
    const hidden = !visible
    hidRef.current = shouldHide

    const call = setBottomNavVisibility(visible)
    call?.catch(() => {
      const sourceTag = sourceRef.current
      ddLogCustomError(
        "webview-bottom-nav-bridge",
        "Webview bottom nav bridge error",
        {
          context: "useHideAppBottomNavWhileBottomSheetOpen",
          hidden,
          source: sourceTag,
        },
      )
      sendWebviewBottomNavErrorEvent(hidden, sourceTag)
    })
  }, [open])
}
